'use client';

import { useState } from 'react';
import { CheckCircle2, ShieldX, Clock } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

export default function DeleteMyDataPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const supabase = createClient();
      const { error: dbError } = await supabase
        .from('data_deletion_requests')
        .insert({ email: email.trim().toLowerCase() });

      if (dbError) throw dbError;
      setSuccess(true);
      setEmail('');
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again or contact us directly.'
      );
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    color: 'white',
    outline: 'none',
    boxSizing: 'border-box' as const,
  };

  return (
    <div style={{ background: '#060B14', minHeight: '100vh' }}>
      {/* Hero */}
      <section
        style={{
          background: '#060B14',
          padding: '88px 40px 72px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 65% 45% at 50% 0%, rgba(249,115,22,0.09), transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            WebkitMaskImage:
              'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)',
            maskImage:
              'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', maxWidth: '700px', margin: '0 auto' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#F97316',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: '18px',
            }}
          >
            Privacy &amp; Data Rights
          </span>
          <h1
            style={{
              fontSize: 'clamp(36px, 5vw, 58px)',
              fontWeight: 900,
              letterSpacing: '-0.04em',
              lineHeight: 1.05,
              color: 'white',
              marginBottom: '16px',
            }}
          >
            Delete Your Data
          </h1>
          <p style={{ fontSize: '17px', color: '#94A3B8', lineHeight: 1.65 }}>
            You have the right to request deletion of all personal data Incade has collected about you.
          </p>
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: '72px 40px 96px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div
          style={{
            maxWidth: '760px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '60px',
          }}
        >
          {/* Info Panel */}
          <div>
            <h2
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'white',
                marginBottom: '24px',
              }}
            >
              What gets deleted
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: '#94A3B8',
                lineHeight: 1.75,
                marginBottom: '32px',
              }}
            >
              When you submit a deletion request, we will permanently remove all personal data
              associated with your account, including any data obtained through Meta (Facebook /
              Instagram), Google, or other connected platforms and third-party integrations.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                {
                  Icon: ShieldX,
                  label: 'Data covered',
                  value:
                    'Account info, ad platform tokens, analytics data, and any data shared via Meta, Google, TikTok, LinkedIn, Snapchat, or Shopify integrations.',
                },
                {
                  Icon: Clock,
                  label: 'Completion time',
                  value:
                    'All data will be permanently deleted within 30 days of your request.',
                },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'rgba(249,115,22,0.1)',
                      border: '1px solid rgba(249,115,22,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <item.Icon size={18} style={{ color: '#F97316' }} />
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#64748B',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        marginBottom: '4px',
                      }}
                    >
                      {item.label}
                    </p>
                    <p style={{ fontSize: '13px', color: '#94A3B8', lineHeight: 1.6 }}>
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Panel */}
          <div>
            {success ? (
              <div
                style={{
                  background: 'rgba(34,197,94,0.05)',
                  border: '1px solid rgba(34,197,94,0.2)',
                  borderRadius: '16px',
                  padding: '48px 32px',
                  textAlign: 'center',
                }}
              >
                <CheckCircle2
                  size={48}
                  style={{ color: '#4ADE80', margin: '0 auto 16px' }}
                />
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: 'white',
                    marginBottom: '12px',
                  }}
                >
                  Request received
                </h3>
                <p style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.7 }}>
                  Your request has been received. We will delete your data within 30 days.
                </p>
              </div>
            ) : (
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '16px',
                  padding: '36px 32px',
                }}
              >
                <h2
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: 'white',
                    marginBottom: '8px',
                  }}
                >
                  Submit a deletion request
                </h2>
                <p
                  style={{
                    fontSize: '13px',
                    color: '#64748B',
                    marginBottom: '28px',
                    lineHeight: 1.6,
                  }}
                >
                  Enter the email address associated with your Incade account. We will process
                  your request and confirm deletion within 30 days.
                </p>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div>
                    <label
                      htmlFor="email"
                      style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#94A3B8',
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      Email address *
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      style={inputStyle}
                      disabled={loading}
                      autoComplete="email"
                    />
                  </div>

                  {error && (
                    <div
                      style={{
                        background: 'rgba(248,113,113,0.08)',
                        border: '1px solid rgba(248,113,113,0.2)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                      }}
                    >
                      <p style={{ fontSize: '13px', color: '#F87171', margin: 0 }}>{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '13px 24px',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: 'white',
                      background: loading
                        ? 'rgba(249,115,22,0.5)'
                        : 'linear-gradient(135deg, #F97316, #EA580C)',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'opacity 0.2s ease',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {loading ? 'Submitting…' : 'Submit Request'}
                  </button>

                  <p
                    style={{
                      fontSize: '12px',
                      color: '#475569',
                      textAlign: 'center',
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                  >
                    Deletion will be completed within 30 days of your request. This action is
                    irreversible and will permanently remove all your data from Incade.
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
